"use client";

import { c as _c } from "react/compiler-runtime";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { roboto } from "@/utils/font";
import { useCallback, useEffect, useState, startTransition } from "react";
export type ModalRenderedContent = (closeModal: (successful?: boolean) => void) => React.ReactNode;

/**
 * The base modal component to be expanded by containers
 * @param handleClose Function, to close the modal window
 * @param content content to show in the modal window
 */
export function Modal(props) {
  const $ = _c(47);
  if (!(props.renderedContent || props.children)) {
    throw new Error("renderedContent or children are needed on modal");
  }
  const {
    open: controlledOpen,
    changedOpen,
    modalDidClose
  } = props;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  let t0;
  if ($[0] !== changedOpen || $[1] !== controlledOpen || $[2] !== modalDidClose) {
    t0 = newState => {
      if (controlledOpen === undefined) {
        setInternalOpen(newState);
      }
      changedOpen?.(newState);
      if (!newState) {
        modalDidClose?.();
      }
    };
    $[0] = changedOpen;
    $[1] = controlledOpen;
    $[2] = modalDidClose;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const setOpen = t0;
  let t1;
  let t2;
  if ($[4] !== open || $[5] !== setOpen) {
    t1 = () => {
      if (!open) {
        return;
      }
      const handleNavigation = () => {
        startTransition(() => {
          setOpen(false);
        });
      };
      const originalPushState = history.pushState;
      const originalReplaceState = history.replaceState;
      history.pushState = function (...t3) {
        const args = t3;
        originalPushState.apply(history, args);
        handleNavigation();
      };
      history.replaceState = function (...t4) {
        const args_0 = t4;
        originalReplaceState.apply(history, args_0);
        handleNavigation();
      };
      window.addEventListener("popstate", handleNavigation);
      return () => {
        history.pushState = originalPushState;
        history.replaceState = originalReplaceState;
        window.removeEventListener("popstate", handleNavigation);
      };
    };
    t2 = [open, setOpen];
    $[4] = open;
    $[5] = setOpen;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
  let t3;
  if ($[8] !== props.preventDismiss) {
    t3 = function handleInteraction(event) {
      if (props.preventDismiss) {
        event.preventDefault();
      }
    };
    $[8] = props.preventDismiss;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleInteraction = t3;
  let t4;
  if ($[10] !== props) {
    t4 = function handleCloseAutoFocus(event_0) {
      if (props.preventCloseAutoFocus) {
        event_0.preventDefault();
      }
      props.modalDidFinishClose?.();
    };
    $[10] = props;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleCloseAutoFocus = t4;
  let t5;
  if ($[12] !== setOpen) {
    t5 = function handleClose() {
      setOpen(false);
    };
    $[12] = setOpen;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleClose = t5;
  let t6;
  if ($[14] !== props.trigger) {
    t6 = props.trigger && <DialogTrigger asChild={true}>{props.trigger}</DialogTrigger>;
    $[14] = props.trigger;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const t7 = props.hideClose;
  const t8 = props.theme && props.theme === "dark" ? "rounded-2xl bg-secondary" : "bg-offWhite";
  let t9;
  if ($[16] !== props.className || $[17] !== t8) {
    t9 = cn("max-w-[calc(100%-6rem)]", roboto.className, t8, props.className);
    $[16] = props.className;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const t10 = props.theme && props.theme === "dark" ? "text-offWhite" : "text-secondary";
  let t11;
  if ($[19] !== t10) {
    t11 = cn("modal-header-title mx-auto my-0 font-bold", t10);
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== props.header) {
    t12 = <DialogTitle className="text-inherit">{props.header}</DialogTitle>;
    $[21] = props.header;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t11 || $[24] !== t12) {
    t13 = <DialogHeader className={t11}>{t12}</DialogHeader>;
    $[23] = t11;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const t14 = !props.disableOverflow && "overflow-y-scroll";
  let t15;
  if ($[26] !== t14) {
    t15 = cn("relative max-h-[80vh] p-2", t14);
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== handleClose || $[29] !== props.renderedContent) {
    t16 = props.renderedContent?.(handleClose);
    $[28] = handleClose;
    $[29] = props.renderedContent;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== props.children || $[32] !== t15 || $[33] !== t16) {
    t17 = <div className={t15}>{t16}{props.children}</div>;
    $[31] = props.children;
    $[32] = t15;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== handleCloseAutoFocus || $[36] !== handleInteraction || $[37] !== props.hideClose || $[38] !== t13 || $[39] !== t17 || $[40] !== t9) {
    t18 = <DialogContent onInteractOutside={handleInteraction} onPointerDownOutside={handleInteraction} onEscapeKeyDown={handleInteraction} hideCloseButton={t7} onCloseAutoFocus={handleCloseAutoFocus} className={t9}>{t13}{t17}</DialogContent>;
    $[35] = handleCloseAutoFocus;
    $[36] = handleInteraction;
    $[37] = props.hideClose;
    $[38] = t13;
    $[39] = t17;
    $[40] = t9;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== open || $[43] !== setOpen || $[44] !== t18 || $[45] !== t6) {
    t19 = <Dialog open={open} onOpenChange={setOpen}>{t6}{t18}</Dialog>;
    $[42] = open;
    $[43] = setOpen;
    $[44] = t18;
    $[45] = t6;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  return t19;
}